import { useState } from "react";
import type { ProfileData, EditableTabProps } from "../../../lib/profileTypes";
import { SectionCard } from "../card/SectionCard";
import { InfoRow } from "../row/InfoRow";

interface PersonalTabProps extends EditableTabProps {
  profile: ProfileData;
  candidate: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
}

export function PersonalTab({
  profile,
  candidate,
  editing,
  onEdit,
  onSave,
  onCancel,
  saving,
}: PersonalTabProps) {
  const [form, setForm] = useState({ ...profile, name: candidate.name || "" });
  const f = (k: string) =>
    ((form as Record<string, unknown>)[k] as string) || "";
  const set = (k: string, v: string) => setForm({ ...form, [k]: v });

  if (!editing) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title="Personal Information" onEdit={onEdit}>
          <InfoRow label="Full Name" value={candidate.name} />
          <InfoRow label="Email" value={candidate.email} />
          <InfoRow label="Phone" value={candidate.phone} />
          <InfoRow label="Date of Birth" value={profile.date_of_birth} />
          <InfoRow label="Gender" value={profile.gender} />
          <InfoRow label="Nationality" value={profile.nationality} />
          <InfoRow label="Marital Status" value={profile.marital_status} />
        </SectionCard>
        <SectionCard title="Address" onEdit={onEdit}>
          <InfoRow label="Address" value={profile.address} />
          <InfoRow label="City" value={profile.city} />
          <InfoRow label="State / Province" value={profile.state} />
          <InfoRow label="Country" value={profile.country} />
          <InfoRow label="Zip Code" value={profile.zip_code} />
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <SectionCard title="Personal Information" editing>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-semibold uppercase text-text-secondary">
              Full Name
            </label>
            <input
              value={f("name")}
              onChange={(e) => set("name", e.target.value)}
              className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2.5 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
            />
          </div>
          {[
            ["date_of_birth", "Date of Birth", "date"],
            ["gender", "Gender", "text"],
            ["nationality", "Nationality", "text"],
            ["marital_status", "Marital Status", "text"],
          ].map(([k, l, t]) => (
            <div key={k}>
              <label className="text-[10px] font-semibold uppercase text-text-secondary">
                {l}
              </label>
              <input
                type={t}
                value={f(k)}
                onChange={(e) => set(k, e.target.value)}
                className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2.5 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
              />
            </div>
          ))}
        </div>
      </SectionCard>
      <SectionCard title="Address" editing>
        <div className="space-y-2">
          {[
            ["address", "Address"],
            ["city", "City"],
            ["state", "State / Province"],
            ["country", "Country"],
            ["zip_code", "Zip Code"],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="text-[10px] font-semibold uppercase text-text-secondary">
                {l}
              </label>
              <input
                value={f(k)}
                onChange={(e) => set(k, e.target.value)}
                className="mt-0.5 w-full rounded-btn border border-border-card bg-white px-2.5 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
              />
            </div>
          ))}
        </div>
      </SectionCard>
      <div className="flex gap-2 lg:col-span-2">
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white hover:bg-rausch disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
